import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import {
  useCompanyProfile,
  useRoleContext,
  useShipmentRoute,
  useSupplierShipmentETAs,
} from "hooks";
import { useSupplierShipmentPDFExport } from "hooks/useSupplierShipmentPDFExport";

import { getSupplierShipmentExportPDFRenderConfig } from "./utils";
import { ClientAppEvent } from "../../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  supplierShipment: SupplierShipment;
}

export function ExportSupplierShipmentToPDFModal(props: Props) {
  const { open, onClose, supplierShipment } = props;
  const posthog = usePostHog();
  const [includeCarrierInfo, setIncludeCarrierInfo] = useState<boolean>(false);
  const shipmentRef = useRef<undefined | SupplierShipment>();
  const { role } = useRoleContext();
  const {
    route,
    error: routeFetchError,
    loading: routeLoading,
    refetchRoute,
  } = useShipmentRoute(supplierShipment?.info.route);
  const { companyProfile, loading: companyProfileLoading } = useCompanyProfile(
    supplierShipment?.booked_carrier?.company ?? null,
  );
  const { etas, loading: etasLoading } = useSupplierShipmentETAs({
    shipmentId: supplierShipment?.info?.id || 0,
  });

  useEffect(() => {
    /*
    This hook keeps track of the shipment ID between renders to ensure
    that the route is re-fetched when another shipment is selected.
    */
    if (
      supplierShipment &&
      shipmentRef.current?.info?.id !== supplierShipment.info.id
    ) {
      shipmentRef.current = supplierShipment;
      refetchRoute();
    }
  }, [supplierShipment, refetchRoute]);

  const { exportSupplierShipmentToPDF, isExporting, error } =
    useSupplierShipmentPDFExport({
      supplierShipment,
    });

  const handleClose = () => {
    setIncludeCarrierInfo(false);
    onClose();
  };

  const carrierInfoAvailable =
    !!supplierShipment?.info?.booked_at && !!supplierShipment?.booked_carrier;
  const optionsTextColor = carrierInfoAvailable
    ? "text.primary"
    : "text.secondary";

  const handleExport = () => {
    const pdfRenderConfig = getSupplierShipmentExportPDFRenderConfig(
      supplierShipment,
      route,
      includeCarrierInfo,
      companyProfile,
      etas,
    );

    posthog?.capture(ClientAppEvent.SHIPMENT_EXPORTED_TO_PDF, {
      export_params: {
        include_price: false, // Never include price for supplier shipments
        include_carrier_info: includeCarrierInfo,
        carrier_info_available: carrierInfoAvailable,
        is_supplier_shipment: true,
      },
      shipment_id: supplierShipment.info.id,
      triggered_by_role: role?.id,
      role_type: role?.type,
    });

    exportSupplierShipmentToPDF(pdfRenderConfig).then(() => {
      handleClose();
    });
  };

  return (
    <Box>
      <Dialog fullWidth open={open} onClose={handleClose}>
        <DialogTitle>Export Supplier Shipment to PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This will export the supplier shipment details and route information
            to a PDF file.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeCarrierInfo}
                onChange={(e) => setIncludeCarrierInfo(e.target.checked)}
                disabled={!carrierInfoAvailable}
                size="medium"
              />
            }
            label={
              <Typography variant="h6" color={optionsTextColor}>
                Include carrier information in export
              </Typography>
            }
          />

          <ErrorTypography error={error} />
          <ErrorTypography error={routeFetchError} />
        </DialogContent>
        <LoadingWrappedNode
          loading={
            isExporting || routeLoading || companyProfileLoading || etasLoading
          }
        >
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleExport}
              autoFocus
              color="primary"
              variant="contained"
            >
              Download PDF
            </Button>
          </DialogActions>
        </LoadingWrappedNode>
      </Dialog>
    </Box>
  );
}
